import {
  Avatar,
  Button,
  Flex,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_right.svg';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron_down.svg';
import { UserPermissonResponse } from 'libs/e1api/permissionmanager';
import { Team } from 'modules/User/workspace.types';
import {
  AutoCompleteInput,
  AutoCompleteInputItem,
  AutoCompleteInputList,
} from 'modules/common/components/AutoCompleteInput';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShareWithTeamInviteModal } from './ShareWithTeamInviteModal';

type ShareWithTeamProps = {
  disabled?: boolean;
  teams?: Team[];
  userPermissions?: UserPermissonResponse['permissions'];
  onShareClick?: (teamIds: string[], done?: () => void) => void;
  onUnShareClick?: (teamId: string, done?: () => void) => void;
  onCopyLink?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onNotify?: (message: string, teamIds: string[]) => void;
  onClose?: () => void;
};

export const ShareWithGroups = ({
  disabled = false,
  teams = [],
  userPermissions,
  onShareClick = () => {},
  onUnShareClick = () => {},
  onCopyLink = () => {},
  onNotify = () => {},
  onClose = () => {},
}: ShareWithTeamProps) => {
  const { t } = useTranslation('common_navbar');
  const [selectedIds, setSelectedIds] = useState<string[]>([]); // This is the selected team ID list on the AutoCompleteInput
  const [notifyTeamIds, setNotifyTeamIds] = useState<string[]>([]); // This is the team ID list that shared
  const [showTeamInviteModal, setShowTeamInviteModal] = useState(false);
  const handleInviteModalClose = () => {
    setShowTeamInviteModal(false);
    setNotifyTeamIds([]);
    onClose();
  };

  const groups = userPermissions?.groups ?? [];
  const owner = userPermissions?.owner;
  const ownerFullName = `${owner?.first_name} ${owner?.last_name}`;

  // Send notification to selected team ids
  const handleNotify = useCallback(
    (message: string) => notifyTeamIds.length && onNotify(message, notifyTeamIds),
    [notifyTeamIds, onNotify],
  );

  const handleInputChange = useCallback((selectedIds: string[]) => setSelectedIds(selectedIds), []);

  const handleShareClick = useCallback(() => {
    if (selectedIds.length === 0) return;

    setNotifyTeamIds(selectedIds);
    onShareClick(selectedIds);
    setShowTeamInviteModal(true);
  }, [selectedIds, onShareClick]);

  const shareWithGroupsTestId = 'share-with-groups';

  return (
    <>
      <Flex flexDir='column' data-testid={shareWithGroupsTestId}>
        <Flex flexDir='column' gap='2'>
          <Flex flexDir='row' justifyContent='space-between' alignItems='center'>
            <Text fontWeight={'semibold'} fontSize='sm' lineHeight='1.25' color={'font.500'}>
              {t('modals.share-panel.invite-team-members.title')}
            </Text>
          </Flex>
          {!disabled && (
            <Flex flexDir='row' justifyContent='space-between' alignItems='start' gap='2'>
              <InputGroup>
                <AutoCompleteInput
                  placeholder={t('modals.share-panel.share-with-groups.input-placeholder')}
                  onChange={handleInputChange}
                  label={'Groups'}
                  multiple
                  inputGroup={
                    <InputRightElement width='2.5rem' height={'40px'}>
                      <Button size='sm' bg={'transparent'} p={0} onClick={handleShareClick}>
                        <ArrowIcon
                          fill={selectedIds.length > 0 ? 'var(--vg-colors-upgrade-500)' : 'var(--vg-colors-font-300)'}
                        />
                      </Button>
                    </InputRightElement>
                  }
                >
                  <AutoCompleteInputList showNoResultsFound>
                    {teams.map((team) => (
                      <AutoCompleteInputItem key={team.id} value={team.id} name={team.team_name}>
                        <Avatar size='sm' name={team.team_name[0]} bg='dark-blue.400' />
                        <Text as='span' fontSize={'sm'} fontWeight={'normal'} color='font.500'>
                          {team.team_name}
                        </Text>
                      </AutoCompleteInputItem>
                    ))}
                  </AutoCompleteInputList>
                </AutoCompleteInput>
              </InputGroup>
            </Flex>
          )}

          {owner && (
            <>
              <Flex flexDir='row' justifyContent='space-between' alignItems='center' mt='2'>
                <Flex alignItems={'center'} gap='2'>
                  <Avatar size='sm' name={ownerFullName} bg='dark-blue.400' color='white' />
                  <div>{ownerFullName}</div>
                </Flex>
                <Text>{t('modals.share-panel.share-with-groups.owner')}</Text>
              </Flex>

              {groups.map((group) => (
                <Flex key={group.team_id} flexDir='row' justifyContent='space-between' alignItems='center' mt='2'>
                  <Flex alignItems={'center'} gap='2'>
                    <Avatar size='sm' name={group.name} bg='dark-blue.400' color='white' />
                    <div>{group.name}</div>
                  </Flex>
                  {disabled ? (
                    <Text>{group.type}</Text>
                  ) : (
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant='outline'
                        size='sm'
                        w='150px'
                        minW='150px'
                        color={'font.500'}
                        borderColor='gray.500'
                        borderRadius='sm'
                        _hover={{ bg: 'hover.blue' }}
                        _active={{ bg: 'hover.blue', borderColor: 'upgrade.blue.700', color: 'upgrade.blue.700' }}
                        rightIcon={<ChevronDownIcon />}
                      >
                        <Text textAlign={'left'} fontWeight={'normal'} textTransform={'capitalize'}>
                          {group.type}
                        </Text>
                      </MenuButton>
                      <MenuList px='2' w='150px' minW='150px'>
                        <MenuItem
                          value='name'
                          _hover={{ bg: 'hover.blue', color: 'upgrade.blue.700' }}
                          _focus={{ bg: 'hover.blue' }}
                          _active={{ bg: 'hover.blue' }}
                          textTransform={'capitalize'}
                          fontSize={'sm'}
                        >
                          {group.type}
                        </MenuItem>
                        <MenuItem
                          value='unshare'
                          _hover={{ bg: 'hover.blue', color: 'upgrade.blue.700' }}
                          onClick={() => onUnShareClick(group.team_id)}
                          fontSize={'sm'}
                        >
                          {t('modals.share-panel.share-with-groups.unshare-option')}
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>
              ))}
            </>
          )}
        </Flex>
      </Flex>

      <ShareWithTeamInviteModal
        isOpen={showTeamInviteModal}
        onNotify={handleNotify}
        onClose={handleInviteModalClose}
        onCopyLink={onCopyLink}
      />
    </>
  );
};

ShareWithGroups.displayName = 'ShareWithGroups';
